import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FacturiExterneCloud } from '../components/compliance/FacturiExterneCloud'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import { Card } from '../components/ui/Card'
import { DatoriiFurnizori } from '../components/compliance/DatoriiFurnizori'
import { Spinner } from '../components/ui/Spinner'
import { Button } from '../components/ui/Button'
import { Field } from '../components/ui/Field'
import { Modal } from '../components/ui/Modal'
import { SpvInvoiceViewer } from '../components/SpvInvoiceViewer'
import { Pagination, type PageSize } from '../components/ui/Pagination'

/**
 * Ce arata ecranul.
 *
 * Creantele au disparut de aici: Tabloul de bord vanzari raspunde la „cine imi datoreaza bani"
 * cu soldul reconciliat pe client. Doua ecrane care raspund la aceeasi intrebare cu cifre
 * calculate diferit sunt mai rele decat un ecran lipsa.
 */
type Kind = 'furnizori-ea' | 'datorii' | 'externe'
type Status = 'unpaid' | 'partial' | 'paid' | 'overdue' | 'all' | 'ea_deschisa' | 'ea_inchisa'

interface FacturaItem {
  id: number
  type: 'FACTURA TRIMISA' | 'FACTURA PRIMITA'
  kind: 'creante' | 'datorii'
  docId: string | null
  issueDate: string | null
  effectiveDueDate: string | null
  cifPartener: string | null
  partenerName: string | null
  totalWithTax: number
  taxAmount: number
  totalNoTax: number
  currency: string | null
  paymentStatus: 'unpaid' | 'partial' | 'paid'
  paidAmount: number
  remainingAmount: number
  /** La o factura in valuta: cursul in lei al facturii; null = fara curs. */
  cursLei?: number | null
  daysToDue: number | null
  paidAt: string | null
  paidNote: string | null
  hasPdf: boolean
  matchedTxIds: number[]
  /** Starea din contabilitate (EA); null = EA n-a răspuns. */
  eaStare?: 'deschisa' | 'inchisa' | null
  eaRest?: number | null
}

interface ListResponse {
  items: FacturaItem[]
  total: number
  totals: {
    creanteOpen: number
    datoriiOpen: number
    creanteOverdue: number
    datoriiOverdue: number
    countCreanteOpen: number
    countDatoriiOpen: number
    countOverdue: number
    inValuta?: { moneda: string; count: number; faraCurs?: number }[]
  }
}

const fmt = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function FacturiRestantePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canRun = permissions.includes('compliance:run')

  /*
    Tabul stă în adresă (`?tab=spv|externe`), ca butonul Back din browser să întoarcă la tabul
    de dinainte, nu să iasă de pe pagină (Alexandru, 14.10.2026: „aici nu am buton de back").
  */
  const [params, setParams] = useSearchParams()
  const tabUrl = params.get('tab')
  const kind: Kind =
    tabUrl === 'spv' ? 'datorii' : tabUrl === 'externe' ? 'externe' : 'furnizori-ea'
  const setKind = (k: Kind) => {
    if (k === kind) return
    const p = new URLSearchParams(params)
    if (k === 'furnizori-ea') p.delete('tab')
    else p.set('tab', k === 'datorii' ? 'spv' : 'externe')
    setParams(p)
  }
  const [status, setStatus] = useState<Status>('all')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [activeId, setActiveId] = useState<number | null>(null)
  /** Factura deschisă ca PDF (cum vine din SPV). */
  const [pdfDe, setPdfDe] = useState<{ id: number; docId: string | null } | null>(null)
  // Ignorat pe server la furnizori; rămâne în cerere pentru exportul CSV.
  const b2bOnly = false
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(50)

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    p.set('kind', kind)
    p.set('status', status)
    if (search.trim()) p.set('search', search.trim())
    p.set('b2bOnly', b2bOnly ? 'true' : 'false')
    p.set('limit', String(pageSize))
    p.set('offset', String((page - 1) * pageSize))
    return p.toString()
  }, [kind, status, search, b2bOnly, page, pageSize])

  // Reset page când se schimbă filtrele
  useEffect(() => {
    setPage(1)
  }, [kind, status, search, b2bOnly])

  const listQuery = useQuery<ListResponse>({
    queryKey: ['facturi-restante', queryString],
    queryFn: async () => (await api.get<ListResponse>(`/facturi-restante?${queryString}`)).data,
    refetchInterval: 60_000,
    // Lista din SPV doar pe tabul ei; celelalte taburi își aduc singure datele.
    enabled: kind === 'datorii',
  })

  const items = listQuery.data?.items ?? []
  const totals = listQuery.data?.totals
  const total = listQuery.data?.total ?? 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Facturi furnizori</h1>
      </div>

      {kind === 'furnizori-ea' && (
        <Card>
          <div className="mb-3 flex gap-1 rounded border border-b4l-border bg-b4l-bg-soft p-0.5">
            <TabButton active onClick={() => setKind('furnizori-ea')}>
              Din contabilitate
            </TabButton>
            <TabButton active={false} onClick={() => setKind('datorii')}>
              Facturi primite (SPV)
            </TabButton>
            <TabButton active={false} onClick={() => setKind('externe')}>
              Facturi externe (cloud)
            </TabButton>
          </div>
          <DatoriiFurnizori />
        </Card>
      )}

      {kind === 'externe' && (
        <Card>
          <div className="mb-3 flex gap-1 rounded border border-b4l-border bg-b4l-bg-soft p-0.5">
            <TabButton active={false} onClick={() => setKind('furnizori-ea')}>
              Din contabilitate
            </TabButton>
            <TabButton active={false} onClick={() => setKind('datorii')}>
              Facturi primite (SPV)
            </TabButton>
            <TabButton active onClick={() => setKind('externe')}>
              Facturi externe (cloud)
            </TabButton>
          </div>
          <FacturiExterneCloud canEdit={canRun} />
        </Card>
      )}

      {kind === 'datorii' && (
        <Card>
          {/* Tabs + filtre */}
          <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-b4l-border pb-3">
            <div className="flex gap-1 rounded border border-b4l-border bg-b4l-bg-soft p-0.5">
              <TabButton active={false} onClick={() => setKind('furnizori-ea')}>
                Din contabilitate
              </TabButton>
              <TabButton active onClick={() => setKind('datorii')}>
                Facturi primite (SPV)
              </TabButton>
              <TabButton active={false} onClick={() => setKind('externe')}>
                Facturi externe (cloud)
              </TabButton>
            </div>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as Status)}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              {/*
                Starea vine din EA (Alexandru, 14.10.2026: „la Achitată nu e afișat nimic"). Plata
                potrivită din extras cerea suma exactă a facturii și nu marca aproape nimic.
              */}
              <option value="all">Toate stările</option>
              <option value="ea_deschisa">Neplătite (deschise în EA)</option>
              <option value="overdue">Restante (deschise în EA, scadență depășită)</option>
              <option value="ea_inchisa">Închise în EA (plătite sau neînregistrate)</option>
              <option value="paid">Plătite din extras (potrivite cu banca)</option>
            </select>

            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setSearch(searchInput)
              }}
              onBlur={() => setSearch(searchInput)}
              placeholder="Caută factura / partener / CIF... (Enter)"
              className="w-64 rounded border border-b4l-border px-3 py-1.5 text-sm"
            />

            {/*
              „Doar B2B" a ieșit (14.10.2026): scotea persoanele fizice dintre clienți, iar la
              furnizori ascundea facturile din SPV fără codul fiscal al furnizorului.
            */}
            <Button
              variant="secondary"
              onClick={() => {
                const exportParams = new URLSearchParams()
                exportParams.set('kind', kind)
                exportParams.set('status', status)
                if (search.trim()) exportParams.set('search', search.trim())
                exportParams.set('b2bOnly', b2bOnly ? 'true' : 'false')
                const url = `/api/facturi-restante/export.csv?${exportParams.toString()}`
                api
                  .get(url, { responseType: 'blob' })
                  .then((res) => {
                    const blob = new Blob([res.data as BlobPart], { type: 'text/csv' })
                    const downloadUrl = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = downloadUrl
                    a.download = `facturi-furnizori-${new Date().toISOString().slice(0, 10)}.csv`
                    document.body.appendChild(a)
                    a.click()
                    a.remove()
                    URL.revokeObjectURL(downloadUrl)
                  })
                  .catch(() => toast.error('Eroare export CSV'))
              }}
              className="!text-xs"
              title="Exportă toate facturile filtrate (toate paginile)"
            >
              ↓ Exportă tot
            </Button>

            <span className="ml-auto text-xs text-b4l-text-muted">
              {total} {total === 1 ? 'factură' : 'facturi'} total
            </span>
          </div>

          {/*
            Cardurile de sumă stau SUB taburi, în tabul lor: deasupra taburilor păreau să fie ale
            întregii pagini (Alexandru, 14.10.2026: „mă cam derutează").
          */}
          {totals && (
            <div className="mb-3 grid grid-cols-2 gap-2">
              <StatCard
                label="De plătit"
                value={fmt(totals.datoriiOpen)}
                sub={`${totals.countDatoriiOpen} facturi · lei, cu TVA`}
                accent="amber"
                active={kind === 'datorii' && status !== 'overdue'}
                onClick={() => {
                  setKind('datorii')
                  setStatus('all')
                }}
              />
              <StatCard
                label="Restante datorii"
                value={fmt(totals.datoriiOverdue)}
                sub="depășit scadența"
                accent="red"
                active={kind === 'datorii' && status === 'overdue'}
                onClick={() => {
                  setKind('datorii')
                  setStatus('overdue')
                }}
              />
              {!!totals.inValuta?.length && (
                <p className="col-span-2 text-xs text-b4l-amber-dark">
                  Conține valută:{' '}
                  {totals.inValuta
                    .map(
                      (v) =>
                        `${v.count} facturi în ${v.moneda}` +
                        (v.faraCurs ? ` (${v.faraCurs} fără curs BNR, neincluse)` : ''),
                    )
                    .join(', ')}{' '}
                  — incluse în sumele de mai sus în lei, la cursul facturii.
                </p>
              )}
            </div>
          )}

          {/* Tabel */}
          {listQuery.isLoading ? (
            <div className="py-6 flex justify-center">
              <Spinner label="Se încarcă facturile…" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-b4l-text-muted">
              Nicio factură pentru filtrele alese.
            </p>
          ) : (
            <div className="max-h-[70vh] overflow-auto">
              {/* Capul de tabel rămâne fix la derulare (Alexandru, 14.10.2026). */}
              <table className="w-full text-sm">
                <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-b4l-surface [&_th]:shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)]">
                  <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                    <th className="py-2 pr-3 font-medium">Tip</th>
                    <th className="py-2 pr-3 font-medium">Mon</th>
                    <th className="py-2 pr-3 font-medium">Număr</th>
                    <th className="py-2 pr-3 font-medium">Data</th>
                    <th className="py-2 pr-3 font-medium">
                      {kind === 'datorii' ? 'Furnizor' : kind === 'creante' ? 'Client' : 'Partener'}
                    </th>
                    <th className="py-2 pr-3 font-medium">Scadență / Plăți</th>
                    <th className="py-2 pr-3 text-right font-medium">Valoare</th>
                    <th className="py-2 pr-3 text-right font-medium">TVA</th>
                    <th className="py-2 pr-3 text-right font-medium">Total</th>
                    <th className="py-2 pr-3 font-medium">În EA</th>
                    <th className="py-2 pr-3 font-medium">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <FacturaRow
                      key={it.id}
                      item={it}
                      onClick={() => setActiveId(it.id)}
                      onPdf={() => setPdfDe({ id: it.id, docId: it.docId })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 && (
            <div className="mt-3">
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={(n) => {
                  setPageSize(n)
                  setPage(1)
                }}
                itemLabel="facturi"
              />
            </div>
          )}
        </Card>
      )}

      {pdfDe && (
        <SpvInvoiceViewer
          inboundId={pdfDe.id}
          pdfPath={`/facturi-restante/${pdfDe.id}/pdf`}
          docId={pdfDe.docId}
          onClose={() => setPdfDe(null)}
        />
      )}

      {activeId && (
        <FacturaDetailModal
          invoiceId={activeId}
          onPdf={(id, docId) => setPdfDe({ id, docId })}
          canEdit={canRun}
          onClose={() => setActiveId(null)}
          onUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ['facturi-restante'] })
            setActiveId(null)
          }}
        />
      )}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`rounded px-3 py-1 text-sm transition ${
        active
          ? 'bg-white font-semibold text-b4l-text shadow-sm ring-1 ring-b4l-border'
          : 'text-b4l-blue-dark underline-offset-2 hover:bg-white/60 hover:underline'
      }`}
    >
      {children}
    </button>
  )
}

function FacturaRow({
  item,
  onClick,
  onPdf,
}: {
  item: FacturaItem
  onClick: () => void
  onPdf: () => void
}) {
  const isOverdue =
    item.paymentStatus !== 'paid' &&
    item.eaStare !== 'inchisa' &&
    item.daysToDue !== null &&
    item.daysToDue < 0

  return (
    <tr
      className={`border-b border-b4l-border last:border-0 transition hover:bg-b4l-bg-soft ${
        isOverdue ? 'bg-b4l-red-light/10' : ''
      }`}
    >
      <td className="py-2 pr-3 text-xs text-b4l-text-muted">F</td>
      <td className="py-2 pr-3 text-xs">{item.currency ?? 'RON'}</td>
      <td
        className={`py-2 pr-3 font-mono text-xs ${
          isOverdue ? 'font-semibold text-b4l-red-dark' : ''
        }`}
      >
        {item.docId ?? `#${item.id}`}
      </td>
      <td className="py-2 pr-3 text-xs">
        {item.issueDate ? new Date(item.issueDate).toLocaleDateString('en-GB') : '—'}
      </td>
      <td className="py-2 pr-3">
        <div className="text-sm">{item.partenerName ?? '—'}</div>
        {item.cifPartener && (
          <div className="text-[10px] text-b4l-text-dim">{item.cifPartener}</div>
        )}
      </td>
      <td className="py-2 pr-3">
        <ScadentaBadge item={item} />
      </td>
      <td className="py-2 pr-3 text-right text-xs tabular-nums">{fmt(item.totalNoTax)}</td>
      <td className="py-2 pr-3 text-right text-xs tabular-nums text-b4l-text-muted">
        {fmt(item.taxAmount)}
      </td>
      <td className="py-2 pr-3 text-right text-sm font-semibold tabular-nums">
        {fmt(item.totalWithTax)}
      </td>
      <td className="py-2 pr-3 text-xs">
        <StareEa item={item} />
      </td>
      <td className="py-2 pr-3">
        <div className="flex gap-1">
          <Button
            onClick={onPdf}
            variant="secondary"
            className="!px-2 !py-1 !text-xs"
            title="Factura ca PDF, așa cum vine din SPV"
          >
            PDF
          </Button>
          <Button onClick={onClick} variant="secondary" className="!px-2 !py-1 !text-xs">
            Detalii
          </Button>
        </div>
      </td>
    </tr>
  )
}

/** Badge cu suma rămasă + zile până/peste scadență. */
function ScadentaBadge({ item }: { item: FacturaItem }) {
  // Închisă în EA: nu mai e nimic de plătit, deci nici zile de întârziere de arătat.
  if (item.eaStare === 'inchisa' && item.paymentStatus !== 'paid') {
    return (
      <span className="text-xs text-b4l-text-dim">
        {item.effectiveDueDate ? new Date(item.effectiveDueDate).toLocaleDateString('en-GB') : '—'}
      </span>
    )
  }
  if (item.paymentStatus === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-b4l-green-light px-2 py-1 text-xs font-medium text-b4l-green-dark">
        ✓ Plătit
      </span>
    )
  }

  const days = item.daysToDue
  const remaining = item.remainingAmount
  // In valuta: suma in moneda facturii si, dedesubt, in lei la cursul ei.
  const inValuta = !!item.currency && !['RON', 'LEI'].includes(item.currency.toUpperCase())
  const moneda = inValuta ? ` ${item.currency}` : ''
  const lei =
    inValuta && item.cursLei != null ? (
      <span className="text-[10px] opacity-80">≈ {fmt(remaining * item.cursLei)} lei</span>
    ) : null

  if (days === null) {
    return (
      <span className="inline-flex items-center rounded bg-b4l-bg px-2 py-1 text-xs text-b4l-text-muted">
        {fmt(remaining)}
        {moneda} / fără scadență {lei}
      </span>
    )
  }

  let bg = 'bg-b4l-blue-light text-b4l-blue-dark'
  let label = `${days} zile`
  if (days < 0) {
    const overdue = Math.abs(days)
    if (overdue > 60) bg = 'bg-b4l-red-light text-b4l-red-dark font-semibold'
    else if (overdue > 30) bg = 'bg-b4l-red-light text-b4l-red-dark'
    else if (overdue > 7) bg = 'bg-b4l-amber-light text-b4l-amber-dark'
    else bg = 'bg-b4l-amber-light text-b4l-amber-dark'
    label = `${days} zile`
  } else if (days <= 7) {
    bg = 'bg-b4l-amber-light text-b4l-amber-dark'
  } else if (days <= 14) {
    bg = 'bg-b4l-blue-light text-b4l-blue-dark'
  }

  const partial = item.paymentStatus === 'partial'

  return (
    <span
      className={`inline-flex flex-col items-start rounded px-2 py-1 text-xs ${bg}`}
      title={
        item.effectiveDueDate
          ? `Scadență: ${new Date(item.effectiveDueDate).toLocaleDateString('en-GB')}`
          : ''
      }
    >
      <span className="tabular-nums">
        {partial && '⚠ '}
        {fmt(remaining)}
        {moneda} / {label}
      </span>
      {lei}
      {partial && (
        <span className="text-[10px] opacity-80">parțial — total {fmt(item.totalWithTax)}</span>
      )}
    </span>
  )
}

function StatCard({
  label,
  value,
  sub,
  accent,
  active,
  onClick,
}: {
  label: string
  value: string
  sub: string
  accent: 'red' | 'amber' | 'blue' | 'green'
  active: boolean
  onClick: () => void
}) {
  const accentClasses = {
    red: 'border-b4l-red-light text-b4l-red-dark',
    amber: 'border-b4l-amber-light text-b4l-amber-dark',
    blue: 'border-b4l-blue-light text-b4l-blue-dark',
    green: 'border-b4l-green-light text-b4l-green-dark',
  }[accent]
  const activeBg = {
    red: 'bg-b4l-red-light/40',
    amber: 'bg-b4l-amber-light/40',
    blue: 'bg-b4l-blue-light/40',
    green: 'bg-b4l-green-light/40',
  }[accent]
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-start rounded-lg border-2 px-3 py-2 text-left transition ${accentClasses} ${
        active ? activeBg : 'bg-white hover:bg-b4l-bg-soft'
      }`}
    >
      <span className="text-[10px] uppercase tracking-wide opacity-80">{label}</span>
      <span className="text-xl font-bold tabular-nums">{value}</span>
      <span className="text-[10px] opacity-70">{sub}</span>
    </button>
  )
}

interface InvoiceDetail extends FacturaItem {
  matchedTransactions?: Array<{
    id: number
    bookingDate: string
    amount: string | number
    currency: string
    description: string | null
    account: { id: number; bank: string; iban: string }
  }>
}

function FacturaDetailModal({
  invoiceId,
  canEdit,
  onClose,
  onUpdated,
  onPdf,
}: {
  invoiceId: number
  /** Deschide PDF-ul facturii (cum vine din SPV). */
  onPdf: (id: number, docId: string | null) => void
  canEdit: boolean
  onClose: () => void
  onUpdated: () => void
}) {
  const [paidAmount, setPaidAmount] = useState('')
  const [paidNote, setPaidNote] = useState('')

  const detailQuery = useQuery<{
    invoice: InvoiceDetail & { rawXml: string | null; pdfPath: string | null }
  }>({
    queryKey: ['factura-detail', invoiceId],
    queryFn: async () => (await api.get(`/facturi-restante/${invoiceId}`)).data,
  })

  const markPaid = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/facturi-restante/${invoiceId}/mark-paid`, {
          paidAmount: parseFloat(paidAmount),
          note: paidNote || null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Marcat plătit')
      onUpdated()
    },
    onError: () => toast.error('Eroare'),
  })

  const unmarkPaid = useMutation({
    mutationFn: async () => (await api.post(`/facturi-restante/${invoiceId}/unmark-paid`)).data,
    onSuccess: () => {
      toast.success('Anulare marcare plătit')
      onUpdated()
    },
  })

  const inv = detailQuery.data?.invoice

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={inv ? `Factura ${inv.docId ?? '#' + inv.id}` : 'Detalii factură'}
      footer={
        <>
          {inv && (
            <Button variant="secondary" onClick={() => onPdf(inv.id, inv.docId ?? null)}>
              Vezi factura (PDF)
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
        </>
      }
    >
      {detailQuery.isLoading && <p className="text-sm">Se încarcă...</p>}
      {inv && (
        <div className="space-y-4 text-sm">
          {/* Header detalii */}
          <div className="grid grid-cols-2 gap-3 rounded border border-b4l-border bg-b4l-bg-soft p-3">
            <Detail label="Tip" value={inv.type} />
            <Detail
              label={inv.kind === 'creante' ? 'Client' : 'Furnizor'}
              value={inv.partenerName ?? '—'}
            />
            <Detail label="CIF" value={inv.cifPartener ?? '—'} />
            <Detail
              label="Data emiterii"
              value={inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('en-GB') : '—'}
            />
            <Detail
              label="Scadență"
              value={
                inv.effectiveDueDate
                  ? new Date(inv.effectiveDueDate).toLocaleDateString('en-GB') +
                    (inv.daysToDue !== null ? ` (${inv.daysToDue} zile)` : '')
                  : '—'
              }
            />
            <Detail
              label="Total cu TVA"
              value={`${fmt(inv.totalWithTax)} ${inv.currency ?? 'RON'}`}
            />
            <Detail
              label="Achitat"
              value={
                inv.paidAmount > 0 ? `${fmt(inv.paidAmount)} (${inv.paymentStatus})` : 'Nimic încă'
              }
            />
            <Detail label="Rămas de plată" value={fmt(inv.remainingAmount)} />
          </div>

          {inv.paidNote && (
            <div className="rounded border border-b4l-blue-light bg-b4l-blue-light/20 p-2 text-xs">
              <strong>Notă plată:</strong> {inv.paidNote}
            </div>
          )}

          {/* Tranzactii bancare matched */}
          {inv.matchedTransactions && inv.matchedTransactions.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-b4l-text-dim">
                Tranzacții bancare ({inv.matchedTransactions.length})
              </div>
              <div className="space-y-1">
                {inv.matchedTransactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between rounded border border-b4l-border bg-white p-2 text-xs"
                  >
                    <div>
                      <div className="font-medium">
                        {new Date(tx.bookingDate).toLocaleDateString('en-GB')} · {tx.account.bank}{' '}
                        {tx.account.iban.slice(-6)}
                      </div>
                      <div className="text-b4l-text-muted">{tx.description}</div>
                    </div>
                    <div className="font-semibold tabular-nums">
                      {fmt(Math.abs(Number(tx.amount)))} {tx.currency}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Marcare manuala */}
          {canEdit && (
            <div className="rounded border border-b4l-border p-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-b4l-text-dim">
                Marcare manuală plată
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="Sumă achitată (RON)"
                  type="number"
                  step="0.01"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  placeholder={fmt(inv.totalWithTax)}
                />
                <Field
                  label="Notă (opțional)"
                  value={paidNote}
                  onChange={(e) => setPaidNote(e.target.value)}
                  placeholder="ex: cash, compensare cu factura X"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  onClick={() => markPaid.mutate()}
                  loading={markPaid.isPending}
                  disabled={!paidAmount || parseFloat(paidAmount) <= 0}
                  className="!text-xs"
                >
                  Marchează plătit
                </Button>
                {inv.paymentStatus !== 'unpaid' && (
                  <Button
                    variant="secondary"
                    onClick={() => unmarkPaid.mutate()}
                    loading={unmarkPaid.isPending}
                    className="!text-xs"
                  >
                    Anulează marcarea
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">{label}</div>
      <div className="break-words">{value}</div>
    </div>
  )
}

/**
 * Starea din contabilitate. „Închisă" nu înseamnă sigur „plătită": o factură care nu e încă
 * înregistrată în EA nu apare nici ea printre datoriile deschise. Deosebirea cere registrul
 * jurnal din EA, pe care API-ul nu-l dă deocamdată — de aceea eticheta le spune pe amândouă.
 */
function StareEa({ item }: { item: FacturaItem }) {
  if (item.eaStare === 'deschisa') {
    return (
      <span className="rounded bg-b4l-amber-light px-2 py-0.5 text-b4l-amber-dark">
        neplătită · rest {fmt(item.eaRest ?? 0)}
      </span>
    )
  }
  if (item.eaStare === 'inchisa') {
    return (
      <span
        className="rounded bg-b4l-bg px-2 py-0.5 text-b4l-text-muted"
        title="Nu apare printre datoriile deschise din EA: e plătită, sau nu e încă înregistrată."
      >
        închisă (plătită / neînregistrată)
      </span>
    )
  }
  return <span className="text-b4l-text-dim">—</span>
}
